import { _ } from "../../../../core/polyglot/polyglot.js";
import { formatAsShortDate } from "../../../../core/helpers/date-formatting-util.js";
import { formatFullWithTwoDecimals } from "../../../../core/helpers/currency-formatting-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ArrayStream from "../../../../core/list/streams/ArrayStream.js";
import GridColumnSpecification from "../../../../core/widgets/list/GridColumnSpecification.js";
import ListSource from "../../../../core/list/ListSource.js";
import makeTableListWidget from "../../../../core/widgets/list/TableListWidget/makeTableListWidget.js";
import PaginationButtonList from "../../../../core/widgets/list/PaginationButtonList/PaginationButtonList.js";

/**
 * Represent ledger search results with 3 tables listing accounts,
 * transactions and ledger entries. The first column of each table
 * contains a link to get more information about the particular search
 * result.
 *
 * @param {Company} company - The company for which the search results
 *   are displayed
 */
export default class SearchResultsWidget extends Widget {
	constructor({ company }) {
		super(...arguments);

		this._company = company;

		this._searchPeriod = undefined;
		this._searchResult = {};

		this._areas = this._makeAreas();
	}

	setSearchResult({ results, period }) {
		this._searchPeriod = period;
		this._searchResult = results;

		this._areas.forEach((area) => {
			area.source = ListSource.makeAndFetch({
				pageSize: 10,
				stream: new ArrayStream({
					data: area.getData(results),
				}),
			});
		});

		this.update();
	}

	renderContentOn(html) {
		this._areas.forEach((area) => {
			html.render(this._areaTableRenderer(area));
		});
	}

	_areaTableRenderer(area) {
		const entries = area.getData(this._searchResult);

		return (html) => {
			if (!entries?.length) return;

			html.h4(area.title);

			const resultTable = makeTableListWidget({
				columnSpecifications: area.columnSpecifications,
				source: area.source,
			});

			const paginationButtons = PaginationButtonList.makeForListSource({
				listSource: area.source,
			});

			html.render(resultTable);
			html.render(paginationButtons);
		};
	}

	_makeAreas() {
		return [
			{
				getData: (searchResult) => searchResult.accounts,
				columnSpecifications: this._makeAccountColumnSpecs(),
				title: _("Accounts"),
			},
			{
				getData: (searchResult) => searchResult.transactions,
				columnSpecifications: this._makeTransactionColumnSpecs(),
				title: _("Transactions"),
			},
			{
				getData: (searchResult) => searchResult.ledgerEntries,
				columnSpecifications: this._makeLedgerEntryColumnSpecs(),
				title: _("Ledger entry"),
			},
		];
	}

	_makeAccountColumnSpecs() {
		return [
			new GridColumnSpecification({
				name: _("Account number"),
				renderer: this._accountLedgerEntryRenderer.bind(this),
				dataTestName: "account-number",
			}),
			new GridColumnSpecification({
				name: _("Year"),
				renderer: ({ fiscalYearId }) =>
					this._company.findYearById(fiscalYearId).getName(),
			}),
			new GridColumnSpecification({
				name: _("Account name"),
				renderer: ({ name }) => name,
			}),
		];
	}

	_makeTransactionColumnSpecs() {
		return [
			new GridColumnSpecification({
				name: _("Ledger entry"),
				renderer: ({ ledgerEntry }) =>
					this._ledgerEntryLinkRenderer(ledgerEntry),
			}),
			new GridColumnSpecification({
				name: _("Date"),
				renderer: ({ date }) => formatAsShortDate(date),
			}),
			new GridColumnSpecification({
				name: _("Text"),
				renderer: ({ text }) => text,
			}),
			new GridColumnSpecification({
				name: _("Projects"),
				renderer: ({ dimensions }) =>
					dimensions.map(({ name }) => name).join(","),
			}),
			new GridColumnSpecification({
				name: _("Type"),
				renderer: ({ type }) => type || "-",
			}),
			new GridColumnSpecification({
				name: _("Amount"),
				cssClass: "text-right",
				headerCssClass: "text-right",
				renderer: ({ amount }) => formatFullWithTwoDecimals(amount),
			}),
		];
	}

	_makeLedgerEntryColumnSpecs() {
		return [
			new GridColumnSpecification({
				name: _("Ledger entry"),
				renderer: this._ledgerEntryLinkRenderer.bind(this),
			}),
			new GridColumnSpecification({
				name: _("Date"),
				renderer: ({ date }) => formatAsShortDate(date),
			}),
			new GridColumnSpecification({
				name: _("Text"),
				renderer: ({ text }) => text,
			}),
		];
	}

	_accountLedgerEntryRenderer({ number, fiscalYearId }) {
		return (html) =>
			html.a(
				{
					target: "_blank",
					href: this._linkTo("account-statement", {
						accountNumber: number,
						period: this._getPeriodIntersectingWithFiscalYear(
							fiscalYearId,
						),
					}),
				},
				number,
			);
	}

	_ledgerEntryLinkRenderer(ledgerEntry) {
		return (html) =>
			html.a(
				{
					target: "_blank",
					href: this._linkTo("ledger-entry", {
						fiscalYearId: ledgerEntry.fiscalYearId,
						ledgerId: ledgerEntry.uniqueIdentifier,
						period: this._getPeriodIntersectingWithFiscalYear(
							ledgerEntry.fiscalYearId,
						),
					}),
				},
				ledgerEntry.uniqueIdentifier,
			);
	}

	_getPeriodIntersectingWithFiscalYear(fiscalYearId) {
		const fiscalYear = this._company.findYearById(fiscalYearId);

		if (!fiscalYear.includesAnyOf(this._searchPeriod)) {
			return "";
		}

		const intersection = fiscalYear.intersect(this._searchPeriod);

		const period = this._company.findPeriodByDates(
			intersection.getStart(),
			intersection.getEnd(),
		);

		return period.toString();
	}
}
